/* global HTMLDivElement */
import { useRef, useState } from "react";
import type { ReactNode } from "react";
import { Check, Plus, X } from "lucide-react";
import type { LifeOsApi } from "../lib/life-os-contract";
import type { Task, TaskStatus, Priority, TaskSubtask } from "../types";
import { BentoDateInput, BentoSelect, ProgressBar, fullDate } from "./bento-ui";
import { TaskScheduleSection } from "./task-schedule-section";
import { DurationInput } from "./duration-input";
import { formatDuration } from "./duration-derive";
import { useEscapeKey } from "../lib/use-escape-key";
import { useFocusTrap } from "../lib/focus-trap";

export type SubTask = TaskSubtask;

const STATUS: Array<[TaskStatus, string]> = [["todo", "Chưa làm"], ["doing", "Đang làm"], ["done", "Xong"]];
const PRIORITY: Array<[Priority, string]> = [["low", "Thấp"], ["medium", "Trung bình"], ["high", "Cao"]];

export function TaskDetail({ task, api, onClose, onDelete, openEntity }: {
  task: Task; api: LifeOsApi; onClose: () => void; onDelete: () => void; openEntity: (kind: string, id: string) => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [newSub, setNewSub] = useState("");
  useEscapeKey(onClose);
  useFocusTrap(panelRef, true);
  const subs = task.subtasks ?? [];
  const doneCount = subs.filter((s) => s.done).length;
  const pct = subs.length ? Math.round((doneCount / subs.length) * 100) : 0;
  const field = "w-full rounded-xl border border-ink-200 bg-white px-3 py-2 text-[13px] outline-none focus:border-brand-500";

  const addSub = () => {
    if (newSub.trim()) {
      const nextSubs = [...subs, { id: crypto.randomUUID(), title: newSub.trim(), done: false }];
      api.actions.updateTask(task.id, { subtasks: nextSubs });
      setNewSub("");
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex justify-end bg-ink-900/40 backdrop-blur-sm" onClick={onClose}>
      <div ref={panelRef} role="dialog" aria-modal="true" aria-label="Chi tiết công việc" className="fade-in h-full w-full max-w-lg overflow-auto bg-white p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <span className="text-[11px] font-bold uppercase tracking-widest text-ink-400">Chi tiết việc</span>
          <button onClick={onClose} aria-label="Đóng" className="grid h-8 w-8 place-items-center rounded-lg text-ink-500 hover:bg-ink-200/60"><X className="h-4 w-4" /></button>
        </div>

        <input value={task.title} onChange={(e) => api.actions.updateTask(task.id, { title: e.target.value })} className="w-full font-display text-[22px] font-bold leading-tight outline-none" />

        {/* D26: free-text description */}
        <textarea
          value={task.description ?? ""}
          onChange={(e) => api.actions.updateTask(task.id, { description: e.target.value })}
          placeholder="Mô tả / ghi chú cho việc này…"
          rows={2}
          className={`mt-2 ${field}`}
        />

        <div className="mt-4 grid grid-cols-2 gap-3">
          <Labeled label="Trạng thái">
            <BentoSelect value={task.status} onChange={(e) => api.actions.updateTask(task.id, { status: e.target.value as TaskStatus })}>{STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</BentoSelect>
          </Labeled>
          <Labeled label="Ưu tiên">
            <BentoSelect value={task.priority} onChange={(e) => api.actions.updateTask(task.id, { priority: e.target.value as Priority })}>{PRIORITY.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</BentoSelect>
          </Labeled>
          <Labeled label="Hạn">
            <BentoDateInput value={task.dueDate} onChange={(e) => api.actions.updateTask(task.id, { dueDate: e.target.value })} className={field} />
          </Labeled>
          <Labeled label="Bắt đầu (ẩn tới ngày)">
            <BentoDateInput value={task.deferDate ?? ""} onChange={(e) => api.actions.updateTask(task.id, { deferDate: e.target.value || undefined })} className={field} />
          </Labeled>
          <Labeled label="Thời lượng">
            <DurationInput value={task.estimateMinutes} onChange={(n) => api.actions.updateTask(task.id, { estimateMinutes: n })} className={field} />
          </Labeled>
          {(task.loggedMinutes ?? 0) > 0 && (
            <div className="col-span-2">
              <p className={`text-[12px] font-semibold ${(task.loggedMinutes ?? 0) > task.estimateMinutes ? "text-amber-600" : "text-ink-500"}`}>
                Đã làm: {formatDuration(task.loggedMinutes ?? 0)} / ước tính {formatDuration(task.estimateMinutes)}
              </p>
            </div>
          )}
          <Labeled label="Dự án">
            <div className="flex items-center gap-1.5">
              <BentoSelect wrapClassName="flex-1" value={task.projectId ?? ""} onChange={(e) => api.actions.updateTask(task.id, { projectId: e.target.value || undefined })}>
                <option value="">— Không —</option>
                {api.state.projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </BentoSelect>
              {task.projectId && <button onClick={() => { onClose(); openEntity("project", task.projectId!); }} className="shrink-0 text-[12px] font-bold text-brand-600">Mở</button>}
            </div>
          </Labeled>
          <Labeled label="Mục tiêu">
            <div className="flex items-center gap-1.5">
              <BentoSelect wrapClassName="flex-1" value={task.goalId ?? ""} onChange={(e) => api.actions.updateTask(task.id, { goalId: e.target.value || undefined })}>
                <option value="">— Không —</option>
                {api.state.goals.map((g) => <option key={g.id} value={g.id}>{g.title}</option>)}
              </BentoSelect>
              {task.goalId && <button onClick={() => { onClose(); openEntity("goal", task.goalId!); }} className="shrink-0 text-[12px] font-bold text-brand-600">Mở</button>}
            </div>
          </Labeled>
        </div>

        {/* D26: tags / contexts */}
        <div className="mt-4">
          <p className="mb-1.5 text-[11px] font-semibold text-ink-500">Thẻ / ngữ cảnh</p>
          {(task.tags?.length ?? 0) > 0 && (
            <div className="mb-2 flex flex-wrap gap-1.5">
              {task.tags!.map((tag) => (
                <span key={tag} className="rounded-full bg-brand-100 px-2.5 py-1 text-[11px] font-semibold text-brand-600">#{tag}</span>
              ))}
            </div>
          )}
          <input
            value={(task.tags ?? []).join(", ")}
            onChange={(e) => api.actions.updateTask(task.id, { tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })}
            placeholder="vd: @nhà, @công-ty, sâu (cách nhau dấu phẩy)"
            className={field}
          />
        </div>

        {/* SUBTASKS / CHECKLIST */}
        <div className="mt-6">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-[11px] font-bold uppercase tracking-widest text-ink-400">Việc con</p>
            <span className="text-[12px] font-semibold text-ink-500">{doneCount}/{subs.length}</span>
          </div>
          {subs.length > 0 && <div className="mb-3"><ProgressBar percent={pct} /></div>}
          <div className="space-y-1.5">
            {subs.map((s) => (
              <div key={s.id} className="group flex items-center gap-2.5 rounded-xl bg-ink-200/30 px-3 py-2">
                <button onClick={() => {
                  const nextSubs = subs.map((x) => (x.id === s.id ? { ...x, done: !x.done } : x));
                  api.actions.updateTask(task.id, { subtasks: nextSubs });
                }} className={`grid h-5 w-5 shrink-0 place-items-center rounded-full border-2 ${s.done ? "border-emerald-500 bg-emerald-500 text-white" : "border-ink-300"}`}>{s.done && <Check className="h-3 w-3" />}</button>
                <span className={`flex-1 text-[13px] ${s.done ? "text-ink-400 line-through" : "font-medium"}`}>{s.title}</span>
                <button onClick={() => {
                  const nextSubs = subs.filter((x) => x.id !== s.id);
                  api.actions.updateTask(task.id, { subtasks: nextSubs });
                }} aria-label="Xoá" className="text-ink-400 opacity-0 group-hover:opacity-100"><X className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <input value={newSub} onChange={(e) => setNewSub(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addSub()} className={field} placeholder="Thêm việc con… (Enter)" />
            <button onClick={addSub} className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-brand-100 text-brand-600"><Plus className="h-4 w-4" /></button>
          </div>
        </div>

        {/* D11/A4: time-block scheduling — passes openEntity for "Mở lịch" navigation */}
        <TaskScheduleSection task={task} api={api} openEntity={openEntity} />

        <div className="mt-6 flex flex-wrap items-center justify-between gap-2 border-t border-ink-200 pt-4 text-[12px] text-ink-500">
          <span>{task.createdAt ? `Tạo · ${fullDate(task.createdAt.slice(0, 10))}` : `Hạn · ${fullDate(task.dueDate)}`}</span>
          <button onClick={() => { onDelete(); onClose(); }} className="rounded-lg bg-rose-50 px-3 py-1.5 text-[12px] font-bold text-rose-500">Xoá việc</button>
        </div>
      </div>
    </div>
  );
}

function Labeled({ label, children }: { label: string; children: ReactNode }) {
  return <label className="block"><span className="mb-1 block text-[11px] font-semibold text-ink-500">{label}</span>{children}</label>;
}
